import React, { type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import { BitbucketIcon } from '@atlaskit/logo';
import { colors } from '@atlaskit/theme';

import { token } from '@atlaskit/tokens';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import commonMessages from '../../../common/messages.tsx';
import {
	CHART_HEIGHT as BAR_CHART_HEIGHT,
	CHART_TITLE_HEIGHT,
	CHART_TITLE_MARGIN_BOTTOM,
} from '../../../common/ui/deployment-frequency-bar-chart/constants.tsx';
import { CHART_HEIGHT as LINE_CHART_HEIGHT } from '../../../common/ui/issues-per-deployment-chart/constants.tsx';
import gitHubIcon from './assets/github.svg';
import gitLabIcon from './assets/gitlab.svg';
import jenkins from './assets/jenkins.svg';
import messages from './messages.tsx';

export const NoProvider = () => {
	const { formatMessage } = useIntl();

	// TODO RLSS-2812 handle darkmode github icon
	return (
		<Wrapper data-test-id="software-deployment-frequency-report.ui.deployment-frequency-bar-chart.no-provider">
			<Container>
				<Title>
					<Heading size="medium">
						<TitleText>{formatMessage(messages.noProviderTitle)}</TitleText>
					</Heading>
					<BitbucketIcon size="xlarge" />
					<IconWrapper>
						<img src={gitHubIcon} alt="GitHub" />
					</IconWrapper>
					<IconWrapper>
						<img src={gitLabIcon} alt="GitLab" />
					</IconWrapper>
					<JenkinsIconWrapper>
						<img src={jenkins} alt="Jenkins" height="50" />
					</JenkinsIconWrapper>
				</Title>
				<Description>{formatMessage(messages.noProviderDescription)}</Description>
				<LearnMoreWrapper>
					<Button
						onClick={(e: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
							fireUIAnalytics(analyticsEvent, 'emptyStateLearnMore');
						}}
						target="_blank"
						href="https://www.atlassian.com/software/jira/guides/developers/ci-cd#get-started"
					>
						{formatMessage(commonMessages.learnMore)}
					</Button>
				</LearnMoreWrapper>
			</Container>
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	height: `${
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		BAR_CHART_HEIGHT + LINE_CHART_HEIGHT + CHART_TITLE_HEIGHT + CHART_TITLE_MARGIN_BOTTOM
	}px`,
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${gridSize * 82}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Title = styled.div({
	marginBottom: token('space.300', '24px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 6}px`,
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleText = styled.div({
	marginTop: 0,
	marginRight: token('space.100', '8px'),
	marginBottom: 0,
	marginLeft: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	marginLeft: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const JenkinsIconWrapper = styled.div({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	marginLeft: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Description = styled.div({
	margin: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N800),
	font: token('font.body'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LearnMoreWrapper = styled.div({
	marginTop: token('space.200', '16px'),
	display: 'flex',
	width: '100%',
	alignItems: 'center',
	justifyContent: 'center',
});
